import {
  Box,
  Button,
  Modal,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@mui/material";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox } from "@mui/material";
import React, { useEffect, useState } from "react";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { useSelector } from "react-redux";
import Close from "./Assets/Images/close.svg";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import {
  AgingSummaryColumnMapper,
  HistoricalPaymentColumnMapper,
  identifyFile,
  mergeFiles,
  readFileData,
  readSheetsForAnalysis,
  readWorkbookForAnalysis,
} from "./utils/fileProcessingUtils";
import { suggestAgingMapping, analyzeInvoiceSheets, analyzeWorkbook } from "./api/companyApi";
import BinIcon from "./Assets/Images/delete.svg";
import Tick from "./Assets/Images/tick.svg";
import PropTypes from "prop-types";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import dayjs from 'dayjs';
import companyApi from "./api/companyApi";
import tenantApi from "./api/tenantApi";
import { publish } from "./events";
import AiClarifyChat from "./components/AiClarifyChat";
import ReceivablesImportWizard from './components/ReceivablesImportWizard';
import { getReceivablesCapabilities } from './api/receivablesApi';

const CANONICAL_AGING_KEYS = [
  "name", "accountID", "address", "current", "aging15", "aging30", "aging45", "aging60",
  "aging90", "aging120", "aging180", "aging365", "aging60Plus", "aging90Plus", "aging120Plus",
  "aging180Plus", "aging365Plus", "age", "collectionAtRisk", "transactionType", "reason",
  "marketSegment", "country", "city", "zipCode", "state", "totalDebt", "AvgCustomerPaymentDays",
];
// Invoice/transaction-level schema the pipeline ingests (processEntries). documentNumber required.
const INVOICE_KEYS = [
  "documentNumber", "ref", "name", "billingName", "accountId", "vatRegistration", "invoiceDate", "dueDate",
  "paymentDate", "invoiceAmount", "amountDue", "currency", "status", "type", "transactionType", "memo",
  "country", "city", "state", "zipCode", "address", "phone", "contactName", "contactEmail",
];
// Build invoice-level records from the chosen sheet's rows + AIArc's mapping. Handles hierarchical
// (grouped) reports like a QuickBooks "A/R Aging Detail": the customer name sits on a group-header
// row and the transaction rows beneath it have a BLANK name that inherits from the header above
// (forward-fill). Group-header/subtotal/total rows (a name/label but no document number) are skipped.
const buildInvoiceRecords = (rows, headerRowIndex, mapping, dropLabels, dateISO) => {
  const headers = (rows[headerRowIndex] || []).map((h) => String(h == null ? "" : h).trim());
  const idx = {};
  INVOICE_KEYS.forEach((k) => { idx[k] = mapping[k] ? headers.findIndex((h) => h === String(mapping[k]).trim()) : -1; });
  const dl = new Set((dropLabels || ["Total"]).map((s) => String(s).toLowerCase().trim()));
  const isDrop = (v) => { const s = String(v || "").toLowerCase().trim(); return !s || dl.has(s) || s.startsWith("total"); };
  const num = (x) => { const n = Number(String(x == null ? "" : x).replace(/[^0-9.-]/g, "")); return isNaN(n) ? 0 : n; };
  const records = [];
  let currentCustomer = "";
  for (let i = headerRowIndex + 1; i < rows.length; i++) {
    const row = rows[i]; if (!row) continue;
    const val = (k) => (idx[k] >= 0 ? String(row[idx[k]] == null ? "" : row[idx[k]]).trim() : "");
    const rowName = val("name");
    const amt = num(val("amountDue")) || num(val("invoiceAmount"));
    let doc = val("documentNumber");
    // A named row with no money is a group header — set the current customer (unless it's a
    // total/non-customer group).
    if (rowName && !isDrop(rowName)) currentCustomer = rowName;
    const effectiveName = rowName || currentCustomer;
    if (!effectiveName || isDrop(effectiveName)) continue;
    if (!doc && !amt) continue;                 // group header / blank — nothing to import
    if (!doc) doc = `${effectiveName.replace(/\s+/g, "").slice(0, 18)}-${i}`; // synthesize a line id
    const rec = {};
    INVOICE_KEYS.forEach((k) => { rec[k] = val(k); });
    rec.name = effectiveName;
    rec.documentNumber = doc;
    rec.accountId = rec.accountId || effectiveName;
    rec.amountDue = rec.amountDue || rec.invoiceAmount;
    rec.invoiceAmount = rec.invoiceAmount || rec.amountDue;
    rec.updatedUpTo = dateISO;
    records.push(rec);
  }
  return records;
};

// Normalize a company name for cross-sheet matching (the Blocked Customers sheet spells names
// slightly differently from AR Detail — "AbTech Industries, Inc" vs "AbTech Industries, Inc.").
const normName = (s) => String(s == null ? "" : s).toLowerCase().replace(/[^a-z0-9]/g, "");

// Pull the tenant's blocked-customer list from a "Blocked Customers" sheet (name + optional amount).
// Returns a Map keyed by normalized name → { name, amount }.
const extractBlockedCustomers = (sheets) => {
  const map = new Map();
  const sheet = (sheets || []).find((s) => /block/i.test(s.name || ""));
  if (!sheet || !Array.isArray(sheet.grid)) return map;
  const numOf = (x) => { const n = Number(String(x == null ? "" : x).replace(/[^0-9.-]/g, "")); return isNaN(n) ? 0 : n; };
  for (const row of sheet.grid) {
    if (!Array.isArray(row)) continue;
    const name = String(row[0] == null ? "" : row[0]).trim();
    if (!name) continue;
    const key = normName(name);
    if (!key || key === "blocked" || /^blocked/i.test(name)) continue; // skip the header cell
    // Amount is usually the next non-empty numeric cell on the row.
    let amount = 0;
    for (let c = 1; c < row.length; c++) { const v = numOf(row[c]); if (v) { amount = v; break; } }
    map.set(key, { name, amount });
  }
  return map;
};

// Apply the blocked list to the extracted invoice records: flag every line of a blocked customer,
// and ADD a line for any blocked customer that has no invoice rows (the user wants them scored 10
// regardless). Blocked customers are always created and forced to ARC 10 downstream.
const applyBlockedCustomers = (records, blockedMap, dateISO) => {
  if (!blockedMap || blockedMap.size === 0) return records;
  const seen = new Set();
  for (const r of records) {
    const key = normName(r.name);
    if (blockedMap.has(key)) { r.isBlocked = true; seen.add(key); }
  }
  for (const [key, { name, amount }] of blockedMap.entries()) {
    if (seen.has(key)) continue;
    records.push({
      name,
      accountId: name,
      documentNumber: `BLOCKED-${name.replace(/\s+/g, "").slice(0, 18)}`,
      amountDue: amount || 0,
      invoiceAmount: amount || 0,
      currency: "USD",
      status: "open",
      isBlocked: true,
      updatedUpTo: dateISO,
    });
  }
  return records;
};

// Pull per-customer annual revenue + invoice count from a "Customer Summary" sheet. That sheet is
// hierarchical: a customer-name row (col 0 filled) carries the customer TOTAL in its right-most
// numeric columns, followed by per-year sub-rows (col 0 blank). Revenue feeds the pulse-index score
// and the recommended-limit logic, so an entity that D&B can't value still gets a grounded number.
const extractCustomerRevenue = (sheets) => {
  const map = new Map();
  const sheet = (sheets || []).find((s) => /customer\s*summary/i.test(s.name || ""));
  if (!sheet || !Array.isArray(sheet.grid)) return map;
  const numOf = (x) => { const n = Number(String(x == null ? "" : x).replace(/[^0-9.-]/g, "")); return isNaN(n) ? 0 : n; };
  for (const row of sheet.grid) {
    if (!Array.isArray(row)) continue;
    const name = String(row[0] == null ? "" : row[0]).trim();
    if (!name) continue; // per-year sub-row (belongs to the customer above)
    const key = normName(name);
    if (!key || /customer\s*summary|unified\s*name|type\s*of\s*service/i.test(name)) continue; // headers
    // The two right-most numeric cells are Total Invoice Count then Total Revenue.
    const nums = row.map(numOf);
    let totalRevenue = 0, invoiceCount = 0;
    for (let c = row.length - 1; c >= 1; c--) { if (nums[c]) { totalRevenue = nums[c]; // last non-zero
        for (let d = c - 1; d >= 1; d--) { if (nums[d]) { invoiceCount = nums[d]; break; } } break; } }
    if (totalRevenue) map.set(key, { revenue: totalRevenue, invoiceCount });
  }
  return map;
};

// Attach revenue/invoice-count to the extracted records by matching customer name.
const applyCustomerRevenue = (records, revenueMap) => {
  if (!revenueMap || revenueMap.size === 0) return records;
  for (const r of records) {
    const hit = revenueMap.get(normName(r.name));
    if (hit) { r.annualRevenue = hit.revenue; if (hit.invoiceCount) r.invoiceCount = hit.invoiceCount; }
  }
  return records;
};

// Build the AR records from AIArc's format-agnostic understanding of the whole upload. Reads the
// full data of each role-classified sheet (across all files) and merges: invoice lines → AR rows,
// blocked sheet/column → blocked flags, revenue sheet → per-customer revenue, and (only if there
// are no invoice lines) a pre-bucketed aging summary as a fallback.
const numCell = (x) => { const n = Number(String(x == null ? "" : x).replace(/[^0-9.-]/g, "")); return isNaN(n) ? 0 : n; };
const colIndexOf = (rows, headerRowIndex, headerText) => {
  if (!headerText) return -1;
  const headers = (rows[headerRowIndex] || []).map((h) => String(h == null ? "" : h).trim());
  return headers.findIndex((h) => h === String(headerText).trim());
};
const looksBlocked = (v) => /^(y|yes|true|1|block|blocked|hold|on hold|credit hold|stop)/i.test(String(v || "").trim());

// Net settlements against invoices. A full ledger (e.g. QuickBooks "A/R Aging Detail") lists each
// invoice AND the Journal/Credit-Memo/Payment rows that later settle it — linked by a reference
// number. Without netting, the scorer keeps the original invoice as "open" AND drops the negative
// settlement (it only counts positive amounts), so a fully-paid customer looks 100% in arrears.
// Grouping by (customer + ref) and netting fixes that: settled invoices become PAYMENT history
// (with the settlement date), partially-paid ones keep only the remaining open balance.
const netInvoicesAndSettlements = (records, dateISO) => {
  const groups = new Map();
  for (const r of records) {
    const amt = parseNum(r.amountDue) || parseNum(r.invoiceAmount);
    const ref = String(r.ref || r.documentNumber || "").trim();
    const key = `${normName(r.name)}|${ref}`;
    if (!groups.has(key)) groups.set(key, { name: r.name, invoiceDate: "", dueDate: "", positive: 0, negative: 0, lastSettle: "", invoiceRec: null, doc: ref });
    const g = groups.get(key);
    if (amt >= 0) {
      g.positive += amt;
      if (!g.invoiceRec) { g.invoiceRec = r; g.invoiceDate = r.invoiceDate || ""; g.dueDate = r.dueDate || ""; }
    } else {
      g.negative += Math.abs(amt);
      const d = r.paymentDate || r.invoiceDate || "";
      if (d > g.lastSettle) g.lastSettle = d; // settlement date (the payment event)
    }
  }
  const open = [], paid = [];
  for (const g of groups.values()) {
    if (g.positive <= 0) continue; // pure credit / unmatched negative — ignore
    const net = g.positive - g.negative;
    if (net <= 0.5) {
      // Fully settled → payment history (a closed record with the settlement date).
      paid.push({
        name: g.name, accountId: g.name,
        documentNumber: `PAY-${String(g.doc || g.name).replace(/\s+/g, "").slice(0, 20)}`,
        invoiceDate: g.invoiceDate, dueDate: g.dueDate,
        paymentDate: g.lastSettle || g.dueDate || g.invoiceDate,
        amountDue: "0", invoiceAmount: String(g.positive),
        status: "close", transactionType: "Payment", updatedUpTo: dateISO,
      });
    } else {
      // Partially open → keep only the remaining open balance, aged by the invoice's own dates.
      const rec = { ...(g.invoiceRec || {}) };
      rec.amountDue = String(net);
      rec.invoiceAmount = String(g.positive);
      rec.status = "open";
      open.push(rec);
    }
  }
  return { open, paid };
};

const buildRecordsFromUnderstanding = async (understanding, files, dateISO, opts = {}) => {
  const sheets = understanding?.sheets || [];
  const fileList = Array.from(files || []);
  const readSheet = (s) => readFileData(fileList[s.fileIndex] || fileList[0], s.sheetIndex || 0);

  const invoiceSheets = sheets.filter((s) => s.role === "invoice_lines");
  const agingSummarySheets = sheets.filter((s) => s.role === "aging_summary");
  const blockedSheets = sheets.filter((s) => s.role === "blocked_customers");
  const revenueSheets = sheets.filter((s) => s.role === "customer_revenue");
  const paymentSheets = sheets.filter((s) => s.role === "payments");

  let records = [];
  let paymentRecords = [];
  const blockedMap = new Map();
  const revenueMap = new Map();

  // 1) Invoice/transaction-level lines (primary AR).
  for (const s of invoiceSheets) {
    const rows = await readSheet(s);
    let recs = buildInvoiceRecords(rows, s.headerRowIndex || 0, s.mappings || {}, s.dropRowLabels || [], dateISO);
    // Net settlements: fold each invoice's settling journal/credit-memo/payment rows into it, so
    // paid invoices become payment history instead of phantom arrears.
    if (opts.netSettlements) {
      const { open, paid } = netInvoicesAndSettlements(recs, dateISO);
      recs = open;
      paymentRecords = paymentRecords.concat(paid);
    }
    records = records.concat(recs);
    // A blocked-status COLUMN inside the invoice sheet → collect those customer names as blocked.
    const bfCol = colIndexOf(rows, s.headerRowIndex || 0, (s.mappings || {}).blockedFlag);
    const nameCol = colIndexOf(rows, s.headerRowIndex || 0, (s.mappings || {}).name);
    if (bfCol >= 0 && nameCol >= 0) {
      let cur = "";
      for (let i = (s.headerRowIndex || 0) + 1; i < rows.length; i++) {
        const r = rows[i]; if (!r) continue;
        const nm = String(r[nameCol] == null ? "" : r[nameCol]).trim(); if (nm) cur = nm;
        if (looksBlocked(r[bfCol]) && cur) blockedMap.set(normName(cur), { name: cur, amount: 0 });
      }
    }
  }

  // 2) Pre-bucketed aging summary — fallback only when there are no invoice lines.
  if (records.length === 0) {
    for (const s of agingSummarySheets) {
      const rows = await readSheet(s);
      const m = s.mappings || {};
      const nameCol = colIndexOf(rows, s.headerRowIndex || 0, m.name);
      const totalCol = colIndexOf(rows, s.headerRowIndex || 0, m.total);
      const bucketCols = ["current", "aging30", "aging60", "aging90"].map((k) => colIndexOf(rows, s.headerRowIndex || 0, m[k]));
      const drop = new Set((s.dropRowLabels || []).map((x) => String(x).toLowerCase().trim()));
      for (let i = (s.headerRowIndex || 0) + 1; i < rows.length; i++) {
        const r = rows[i]; if (!r) continue;
        const nm = nameCol >= 0 ? String(r[nameCol] == null ? "" : r[nameCol]).trim() : "";
        if (!nm || drop.has(nm.toLowerCase()) || nm.toLowerCase().startsWith("total")) continue;
        let amount = totalCol >= 0 ? numCell(r[totalCol]) : 0;
        if (!amount) amount = bucketCols.reduce((a, c) => a + (c >= 0 ? numCell(r[c]) : 0), 0);
        if (!amount) continue;
        records.push({ name: nm, accountId: nm, documentNumber: `AGING-${nm.replace(/\s+/g, "").slice(0, 18)}-${i}`, amountDue: amount, invoiceAmount: amount, currency: understanding.currency || "USD", status: "open", updatedUpTo: dateISO });
      }
    }
  }

  // 3) Blocked-customer list sheet(s).
  for (const s of blockedSheets) {
    const rows = await readSheet(s);
    const m = s.mappings || {};
    const nameCol = Math.max(0, colIndexOf(rows, s.headerRowIndex || 0, m.name));
    const amtCol = colIndexOf(rows, s.headerRowIndex || 0, m.amountDue);
    for (let i = (s.headerRowIndex || 0) + 1; i < rows.length; i++) {
      const r = rows[i]; if (!r) continue;
      const nm = String(r[nameCol] == null ? "" : r[nameCol]).trim();
      if (!nm || /^blocked$/i.test(nm)) continue;
      blockedMap.set(normName(nm), { name: nm, amount: amtCol >= 0 ? numCell(r[amtCol]) : 0 });
    }
  }

  // 4) Per-customer revenue sheet(s).
  for (const s of revenueSheets) {
    const rows = await readSheet(s);
    const m = s.mappings || {};
    const nameCol = Math.max(0, colIndexOf(rows, s.headerRowIndex || 0, m.name));
    const revCol = colIndexOf(rows, s.headerRowIndex || 0, m.annualRevenue);
    for (let i = (s.headerRowIndex || 0) + 1; i < rows.length; i++) {
      const r = rows[i]; if (!r) continue;
      const nm = String(r[nameCol] == null ? "" : r[nameCol]).trim();
      if (!nm) continue;
      // If revenue column not identified, take the right-most numeric cell on the row.
      let rev = revCol >= 0 ? numCell(r[revCol]) : 0;
      if (!rev) for (let c = r.length - 1; c > nameCol; c--) { const v = numCell(r[c]); if (v) { rev = v; break; } }
      if (rev) revenueMap.set(normName(nm), { revenue: rev, invoiceCount: 0 });
    }
  }

  // 5) Payment history (paid invoices) — closed records with a payment date, keyed to the same
  //    customer. These become the CLOSED entries underwriting needs to compute the payment index
  //    (Paydex) from real late-payment behavior; without them Paydex defaults to the worst (10).
  for (const s of paymentSheets) {
    const rows = await readSheet(s);
    const recs = buildInvoiceRecords(rows, s.headerRowIndex || 0, s.mappings || {}, s.dropRowLabels || [], dateISO);
    for (const r of recs) {
      r.status = "close";                 // paid → a closed entry
      r.transactionType = "Payment";
      r.amountDue = "0";                  // fully paid, nothing open
      r.documentNumber = String(r.documentNumber || "").startsWith("PAY") ? r.documentNumber : `PAY-${r.documentNumber}`;
    }
    paymentRecords = paymentRecords.concat(recs);
  }

  applyBlockedCustomers(records, blockedMap, dateISO);
  applyCustomerRevenue(records, revenueMap);
  return { records, paymentRecords };
};

// Friendly labels + colors for the sheet roles AIArc assigns, shown in the understanding panel.
const ROLE_META = {
  invoice_lines: { label: "AR invoices", color: "#2e7d32" },
  payments: { label: "Payments", color: "#0277bd" },
  aging_summary: { label: "Aging summary", color: "#6a1b9a" },
  blocked_customers: { label: "Blocked customers", color: "#b3261e" },
  customer_revenue: { label: "Revenue", color: "#b7791f" },
};

const toMoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const parseNum = (x) => { const n = Number(String(x == null ? "" : x).replace(/[^0-9.-]/g, "")); return isNaN(n) ? 0 : n; };

// Reconciliation: compare the sum of extracted rows to the file's own grand-total row (found via
// a drop label). A big gap usually means subtotal/parent (rollup) rows that double-count.
const computeReconciliation = (rows, headerRowIndex, mapping, dropLabels, agingData) => {
  const headers = (rows[headerRowIndex] || []).map((h) => String(h == null ? "" : h).trim());
  const idxOf = (col) => (col ? headers.findIndex((h) => h === String(col).trim()) : -1);
  const nameCol = idxOf(mapping.name), totalCol = idxOf(mapping.totalDebt);
  const bucketKeys = ["current", "aging15", "aging30", "aging45", "aging60", "aging90", "aging120", "aging180", "aging365", "aging60Plus", "aging90Plus", "aging120Plus", "aging180Plus", "aging365Plus"];
  let extractedSum = 0;
  (agingData || []).forEach((r) => { extractedSum += r.totalDebt ? parseNum(r.totalDebt) : bucketKeys.reduce((a, k) => a + parseNum(r[k]), 0); });
  let grandTotal = 0;
  if (nameCol >= 0 && totalCol >= 0) {
    const dl = new Set((dropLabels || ["Total"]).map((s) => String(s).toLowerCase().trim()));
    for (let i = headerRowIndex + 1; i < rows.length; i++) {
      const nm = String(rows[i]?.[nameCol] == null ? "" : rows[i][nameCol]).trim().toLowerCase();
      if (dl.has(nm)) { grandTotal = parseNum(rows[i][totalCol]); break; }
    }
  }
  const mismatch = grandTotal > 0 && Math.abs(extractedSum - grandTotal) / grandTotal > 0.02;
  return { extractedSum, grandTotal, mismatch };
};

export const validateColumnMapping = (mapping, requiredFields) => {
  return requiredFields.every((field) => mapping[field]);
};

const style = {
  // The SCROLL lives on the overlay wrapper below (overflowY:auto). `margin:auto` centers this card
  // when it's short and lets the overlay scroll it when it's tall — the reliable center-or-scroll
  // pattern. (Capping the card's own height + overflow didn't work: nested content fought it and the
  // modal overflowed the screen with no scrollbar.) position:relative anchors the close (X) button.
  position: "relative",
  width: "833px",
  maxWidth: "95vw",
  margin: "auto",
  bgcolor: "background.paper",
  borderRadius: "20px",
  boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
};

const modalStyle = {
  backdropFilter: "blur(2px)",
  backgroundColor: "rgb(255, 255, 255, 0.5)",
};

const stepperStyle = {
  background: "#5186EC1A",
  height: "82px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
};

const dropzoneStyle = {
  width: "60%",
  border: "2px dashed #5186EC",
  padding: "20px",
  borderRadius: "8px",
  textAlign: "center",
  cursor: "pointer",
  margin: "20px auto",
};

const buttonStyle = {
  mt: 2,
  backgroundColor: "#5186EC",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#4069D0",
  },
  borderRadius: "8px",
};

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[700] : "#E9E9E9",
  zIndex: 1,
  color: "#fff",
  width: 45,
  height: 45,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: "0px 3px 6px #00000029",
  ...(ownerState.active && {
    backgroundColor: "#2F3D63",
    boxShadow: "0px 3px 6px #00000029",
    border: "2px solid #5186EC",
  }),
  ...(ownerState.completed && {
    backgroundColor: "#2F3D63",
    boxShadow: "0px 3px 6px #00000029",
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}>
      {completed ? (
        <img src={Tick} width={25} alt='' />
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <span
            style={{
              color: active ? "#fff" : "#777A82",
              fontSize: "18px",
              fontWeight: "500",
            }}>
            {icon}
          </span>
        </div>
      )}
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  completed: PropTypes.bool,
  icon: PropTypes.node,
};

const FileUploadModal = (props) => {
  const { open, close } = props;
  const [files, setFiles] = useState([]);
  const [tenantId, setTenantId] = useState();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [fileTypes, setFileTypes] = useState({});
  const [columnMappings, setColumnMappings] = useState([]);
  const [headerOffsets, setHeaderOffsets] = useState({}); // per-file real-header row index (AI-detected)
  const [dropLabels, setDropLabels] = useState({});       // per-file total/subtotal row labels to skip
  const [dateSelections, setDateSelections] = useState({});
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [agingUpdatedUpTo, setAgingUpdatedUpTo] = useState(null);

  const steps =
    tenantId === "300358df-22a8-471b-863e-2bb84b5ef948" ||
    tenantId === "notenant"
      ? ["Select Files", "Completed"]
      : ["Select Files", "Confirm Files", "Completed"];

  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenant = useSelector((state) => state.userInfo.tenantId);
  const URLTenant = getTenantIdFromURL();

  const selectTenantId = () => {
    if (isAdmin) setTenantId(URLTenant);
    else setTenantId(currentTenant);
  };

  const handleAccordionChange = (fileName) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? fileName : false);
  };

  const handleDateChange = (newDate) => {
    if (currentFile) {
      const selectedDate = new Date(newDate);
      const utcDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()));
      
      console.log("Aging Updated Up To:", agingUpdatedUpTo);
      console.log("selectedDate (UTC):", utcDate);

        if (utcDate && utcDate < new Date(agingUpdatedUpTo)) {
            toast.error(`The selected date for ${currentFile} must be on or after ${new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(new Date(agingUpdatedUpTo))}.`);
            return;
        }
        setDateSelections((prev) => ({ ...prev, [currentFile]: utcDate.toISOString() }));
    }
    setOpenDatePicker(false);
  };

  const handleCalendarClick = (fileName) => {
    setCurrentFile(fileName);
    setOpenDatePicker(true);
  };

  const getLatestDate = () => {
    const selectedDates = Object.values(dateSelections).filter((date) => date);

    const latestDate =
      selectedDates.length === 0
        ? new Date()
        : selectedDates.reduce((latest, current) =>
          new Date(current) > new Date(latest) ? current : latest
        );

    return formatDate(latestDate);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}_${month}_${year}`;
  };

  const handleClose = () => {
    setFiles([]);
    setFileTypes({});
    setColumnMappings([]);
    setDateSelections({});
    setActiveStep(0);
    setLoading(false);
    setCurrentFile(null);
    setOpenDatePicker(false);
    // Tell the dashboard to reload immediately so it reflects the just-imported/replaced AR instead
    // of showing the stale previous table until the next 15s auto-refresh.
    try { publish("ar_dashboard_refresh"); } catch (e) { /* non-fatal */ }
    setPreview(null);
    setUnderstanding(null);
    setGuidanceInput("");
    setPopulateLog(null);
    setScoring(null);
    setPopulating(false);
    setImportId(null);
    setCancelling(false);
    setClarify(null);
    close();
  };

  useEffect(() => {
    selectTenantId();
  }, [isAdmin, currentTenant, URLTenant]);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const tenantInfo = await tenantApi.getTenantById(tenantId);
        setAgingUpdatedUpTo(tenantInfo.agingUpdatedUpTo);
      } catch (error) {
        console.error("Error fetching tenant info:", error);
      }
    };
    if (tenantId) {
      fetchTenantInfo();
    }
  }, [tenantId]);

  const onDrop = async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      const fileType = await identifyFile(file);
      setFileTypes((prevTypes) => ({ ...prevTypes, [file.name]: fileType }));
    }
    setFiles((prevFiles) => [
      ...prevFiles,
      ...acceptedFiles.filter(
        (newFile) =>
          !prevFiles.some((existingFile) => existingFile.name === newFile.name)
      ),
    ]);
  };

  const handleFileTypeChange = (fileName, newType) => {
    setFileTypes((prevTypes) => ({ ...prevTypes, [fileName]: newType }));
  };

  const CANONICAL_KEYS = CANONICAL_AGING_KEYS;

  // "Analyze with Claude": for each uploaded file, read its rows, ask Claude's best model to detect
  // the file type and map every column to CrediArc's canonical aging schema, then set the file type
  // + column mapping automatically. The user reviews on the next step, then the existing pipeline
  // fills the AR page/tables. Accurate-by-construction: Claude derives the mapping, and the numbers
  // are applied deterministically from the file (Claude never transcribes amounts itself).
  // State-independent upload for the real-tenant path (mirrors handleUpload's else-branch), so the
  // AIArc flow can process immediately with locally-computed config without waiting on setState.
  const performUpload = async ({ localFileTypes, localColumnMappings, localHeaderOffsets, localDropLabels, localDateSelections, formattedDate }) => {
    await tenantApi.updateTenant(tenantId, { agingUpdatedUpTo: new Date(formattedDate).toISOString() });
    const tenantInfo = await tenantApi.getTenantById(tenantId);
    const arThreshold = tenantInfo.arThreshold;
    const dateTag = formattedDate.split("-").reverse().join("_"); // yyyy-mm-dd -> dd_mm_yyyy (filename)
    const { agingData, paymentData } = await mergeFiles(files, localFileTypes, localColumnMappings, localDateSelections, localHeaderOffsets, localDropLabels);
    let agingKey = null, paymentKey = null;
    if (agingData && agingData.length > 0) {
      agingKey = await uploadFileToGCS(new File([JSON.stringify(agingData)], `${dateTag}_aging_data.json`, { type: "application/json" }), "aging");
    }
    if (paymentData && paymentData.length > 0) {
      paymentKey = await uploadFileToGCS(new File([JSON.stringify(paymentData)], `${dateTag}_payment_data.json`, { type: "application/json" }), "payment");
    }
    if (agingKey || paymentKey) {
      // Backend processing is heavy + async (it builds a summary per debtor). Do NOT let a slow/
      // timed-out response fail the flow — the file is uploaded and processing is triggered.
      try {
        await companyApi.processAgingFiles(tenantId, agingKey, paymentKey, formattedDate, arThreshold);
      } catch (procErr) {
        console.error("[AIArc import] processAgingFiles response error (processing continues on the backend):", procErr?.message || procErr);
      }
    }
    return { agingCount: agingData?.length || 0, paymentCount: paymentData?.length || 0, uploaded: !!(agingKey || paymentKey) };
  };

  const [analyzing, setAnalyzing] = useState(false);
  const [understanding, setUnderstanding] = useState(null); // { result, formattedDate, messages } — what AIArc understood about the upload
  const [understandingBusy, setUnderstandingBusy] = useState(false);
  const [guidanceInput, setGuidanceInput] = useState("");
  // AR import options the user can toggle in the review panel.
  const [ageByStatement, setAgeByStatement] = useState(true); // age the AR by the file's "as of" date, not today
  const [netSettlements, setNetSettlements] = useState(true); // fold settling journals/payments into their invoices
  const [replaceExisting, setReplaceExisting] = useState(true); // wipe the existing AR table before importing
  const [clarify, setClarify] = useState(null); // { file, grid, seed, cfg } when AIArc needs input
  const [clarifyImporting, setClarifyImporting] = useState(false);
  const [preview, setPreview] = useState(null);       // { records, file, formattedDate, summary, warnings }
  const [populateLog, setPopulateLog] = useState(null); // [{ ts, msg, status }]
  const [scoring, setScoring] = useState(null); // { total, scored, inProgressNames } — live underwriting progress
  const [populating, setPopulating] = useState(false);
  const [importId, setImportId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Stop the running import and roll back everything it created.
  const cancelImport = async () => {
    if (!importId) return;
    setCancelling(true);
    const log = [...(populateLog || [])];
    const push = (msg, status = "info") => { log.push({ ts: new Date().toLocaleTimeString(), msg, status }); setPopulateLog([...log]); };
    push("Stopping the import and rolling back…", "warn");
    try {
      const res = await companyApi.cancelAgingImport(importId, tenantId);
      const recs = Number(res.recordsDeleted || 0), comps = Number(res.companiesDeleted || 0);
      push(`Rolled back — removed ${recs.toLocaleString()} AR line${recs === 1 ? "" : "s"} and ${comps} customer${comps === 1 ? "" : "s"}.`, "done");
      push("Import stopped. Any customers still mid-processing are removed as they complete.", "done");
      publish("ar_import_progress", { tenantId, importId, status: "cancelled" });
      publish("ar_dashboard_refresh", { tenantId, importId });
    } catch (e) {
      push(`Stop failed: ${e?.response?.data?.message || e?.message || "unknown error"}`, "error");
    } finally {
      setCancelling(false);
    }
  };

  // Build the preview summary for a set of extracted records.
  const summarizeRecords = (records) => {
    const uniq = new Set(records.map((r) => r.name)).size;
    const total = records.reduce((a, r) => a + (parseNum(r.amountDue) || parseNum(r.invoiceAmount)), 0);
    return { count: records.length, customers: uniq, total };
  };

  // Run the actual import, streaming a human-readable log the user can watch.
  const startPopulate = async () => {
    if (!preview) return;
    const { records, formattedDate, paymentRecords } = preview;
    const impId = `imp_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    setImportId(impId);
    publish("ar_import_started", { tenantId, importId: impId });
    publish("ar_dashboard_refresh", { tenantId, importId: impId });
    setScoring(null);
    setPopulating(true);
    const log = [];
    const at = () => new Date().toLocaleTimeString();
    const push = (msg, status = "info") => { log.push({ ts: at(), msg, status }); setPopulateLog([...log]); };
    try {
      const s = summarizeRecords(records);
      push(`Extracted ${s.count.toLocaleString()} AR lines across ${s.customers} customers · total open balance ${toMoney(s.total)}.`, "done");
      push("Recording the statement date…");
      await tenantApi.updateTenant(tenantId, { agingUpdatedUpTo: new Date(formattedDate).toISOString() });
      const tenantInfo = await tenantApi.getTenantById(tenantId);
      const arThreshold = tenantInfo.arThreshold;
      push("Statement date recorded.", "done");
      push("Uploading extracted AR lines to secure storage…");
      const dateTag = formattedDate.split("-").reverse().join("_");
      const agingKey = await uploadFileToGCS(new File([JSON.stringify(records)], `${dateTag}_aging_data.json`, { type: "application/json" }), "aging");
      push("AR lines uploaded.", "done");
      // Payment history (paid invoices) → uploaded separately as payment data so it's stored as CLOSED
      // entries. That gives underwriting real late-payment behavior to compute the payment index
      // (Paydex) from, instead of defaulting everyone to the worst score.
      let paymentKey = null;
      if (Array.isArray(paymentRecords) && paymentRecords.length) {
        push(`Uploading ${paymentRecords.length.toLocaleString()} payment-history records…`);
        paymentKey = await uploadFileToGCS(new File([JSON.stringify(paymentRecords)], `${dateTag}_payment_data.json`, { type: "application/json" }), "payment");
        push("Payment history uploaded — the payment index will reflect real behavior.", "done");
      }
      if (replaceExisting) push("The existing AR customers will be removed before this file is imported.", "info");
      if (ageByStatement) push(`Aging will be calculated as of the statement date (${formattedDate}), not today.`, "info");
      push("Storing the lines and queuing customers for scoring…");
      try {
        await companyApi.processAgingFiles(tenantId, agingKey, paymentKey, formattedDate, arThreshold, impId, replaceExisting, ageByStatement);
        push("Upload accepted — the server is now processing in the background.", "done");
      } catch (e) {
        push("The processing request timed out, but the upload succeeded and the server keeps processing in the background.", "warn");
      }

      // Poll REAL backend progress off the import tracker doc — no more guessing. This surfaces the
      // actual counts (lines stored, customers created, errors) and, if it stalls, exactly why.
      push("Tracking progress…");
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const seen = { stage: null, stored: -1, accounts: false, created: false, replaced: false };
      let done = false, reachedCompleted = false;
      for (let i = 0; i < 240 && !done; i++) {
        await sleep(3000);
        let st;
        try { st = await companyApi.getAgingImportStatus(impId); } catch { continue; }
        if (!st || st.status === "unknown") continue;
        publish("ar_import_progress", { tenantId, importId: impId, status: st.status, stage: st.stage });
        publish("ar_dashboard_refresh", { tenantId, importId: impId });
        if (st.replacedCompanies != null && !seen.replaced) { seen.replaced = true; push(`Replaced the previous AR table — removed ${Number(st.replacedCompanies).toLocaleString()} existing customer${Number(st.replacedCompanies) === 1 ? "" : "s"} and ${Number(st.replacedRecords || 0).toLocaleString()} old line records.`, "done"); }
        if (st.linesStored != null && Number(st.linesStored) !== seen.stored) {
          seen.stored = Number(st.linesStored);
          const total = Number(st.rowsTotal || 0);
          push(total
            ? `Stored ${seen.stored.toLocaleString()} of ${total.toLocaleString()} line records (${Math.round((Number(st.rowsProcessed || seen.stored) / total) * 100)}%).`
            : `Stored ${seen.stored.toLocaleString()} AR line records in the database.`, "done");
        }
        if (st.uniqueAccounts != null && !seen.accounts) { seen.accounts = true; push(`Derived ${Number(st.uniqueAccounts).toLocaleString()} unique customers from the lines.`, "done"); }
        if (st.stage && st.stage !== seen.stage) { seen.stage = st.stage; if (st.stageDetail) push(st.stageDetail); }
        if (st.companiesCreated != null && !seen.created) {
          seen.created = true;
          const c = Number(st.companiesCreated || 0), u = Number(st.companiesUpdated || 0), er = Number(st.companyErrors || 0);
          push(`Customers processed — ${c} created, ${u} updated${er ? `, ${er} failed` : ""}.`, er && !c && !u ? "error" : "done");
          if (er && Array.isArray(st.errorDetails) && st.errorDetails.length) push(`First errors: ${st.errorDetails.slice(0, 3).join(" | ")}`, "warn");
        }
        if (st.status === "completed") { reachedCompleted = true; done = true; }
        else if (st.status === "failed") { push(`Import stopped: ${st.error || "unknown reason"}`, "error"); done = true; }
        else if (st.status === "stalled") { push(`Import stalled: ${st.error || "no backend progress was detected"}`, "error"); done = true; }
        else if (st.status === "cancelled") { done = true; }
      }

      // Scoring phase: follow underwriting company-by-company with a live progress bar + counter.
      if (reachedCompleted) {
        push("Every customer created & queued. Underwriting scores each one now…", "info");
        const announced = new Set();
        let lastScored = -1, scoreDone = false;
        for (let j = 0; j < 300 && !scoreDone; j++) {
          let sp;
          try { sp = await companyApi.getAgingScoringProgress(impId); } catch { await sleep(4000); continue; }
          setScoring({ total: sp.total || 0, scored: sp.scored || 0, inProgressNames: sp.inProgressNames || [] });
          publish("ar_import_progress", { tenantId, importId: impId, status: "underwriting", ...sp });
          publish("ar_dashboard_refresh", { tenantId, importId: impId });
          (sp.inProgressNames || []).forEach((nm) => { if (nm && !announced.has(nm)) { announced.add(nm); push(`Underwriting ${nm}…`); } });
          if ((sp.scored || 0) !== lastScored) { lastScored = sp.scored || 0; push(`Scored ${sp.scored || 0} of ${sp.total || 0} customers.`, "done"); }
          if ((sp.total || 0) > 0 && (sp.scored || 0) >= sp.total) { push("All customers scored — the AR table is up to date.", "done"); scoreDone = true; break; }
          await sleep(4000);
        }
        if (!scoreDone) push("Scoring continues in the background — you can close this; the table fills in as each customer completes.", "info");
      } else if (!done) {
        push("Still processing on the server — you can safely close this; it continues in the background.", "info");
      }
    } catch (e) {
      push(`Import failed: ${e?.response?.data?.message || e?.message || "unknown error"}`, "error");
    } finally {
      setPopulating(false);
      publish("ar_import_progress", { tenantId, importId: impId, status: "finished" });
      publish("ar_dashboard_refresh", { tenantId, importId: impId });
    }
  };

  // Upload pre-built invoice-level records straight to the pipeline (bypasses the mapper).
  const uploadAgingRecords = async (records, formattedDate) => {
    await tenantApi.updateTenant(tenantId, { agingUpdatedUpTo: new Date(formattedDate).toISOString() });
    const tenantInfo = await tenantApi.getTenantById(tenantId);
    const arThreshold = tenantInfo.arThreshold;
    const dateTag = formattedDate.split("-").reverse().join("_");
    const agingKey = await uploadFileToGCS(new File([JSON.stringify(records)], `${dateTag}_aging_data.json`, { type: "application/json" }), "aging");
    try {
      await companyApi.processAgingFiles(tenantId, agingKey, null, formattedDate, arThreshold);
    } catch (procErr) {
      console.error("[AIArc import] processAgingFiles error (processing continues on the backend):", procErr?.message || procErr);
    }
    return { count: records.length };
  };

  // Apply AIArc's resolved invoice mapping from the clarification chat, then import.
  const applyResolvedAndImport = async (res) => {
    if (!clarify) return;
    const c = clarify;
    setClarifyImporting(true);
    try {
      const mapping = {};
      INVOICE_KEYS.forEach((k) => { mapping[k] = ""; });
      Object.entries(res.mappings || {}).forEach(([k, v]) => {
        const col = v && typeof v === "object" ? v.column : v;
        if (col && Object.prototype.hasOwnProperty.call(mapping, k)) mapping[k] = String(col);
      });
      const hIdx = Number.isInteger(res.headerRowIndex) ? res.headerRowIndex : c.cfg.headerOffset;
      const drop = res.dropRowLabels && res.dropRowLabels.length ? res.dropRowLabels : c.cfg.dropLabels;
      const rows = await readFileData(c.file, c.cfg.sheetIndex || 0);
      const records = buildInvoiceRecords(rows, hIdx, mapping, drop, c.cfg.dateISO);
      applyBlockedCustomers(records, c.blockedMap, c.cfg.dateISO);
      applyCustomerRevenue(records, c.revenueMap);
      setClarifyImporting(false);
      if (records.length === 0) { toast.error("Still couldn't extract rows — try mapping the columns manually."); return; }
      // Show the extracted data for approval before import.
      setClarify(null);
      setPreview({ records, file: c.file, formattedDate: c.cfg.formattedDate, summary: summarizeRecords(records), warnings: [] });
    } catch (e) {
      setClarifyImporting(false);
      toast.error("Import failed — you can map the columns manually.");
    }
  };

  // "Analyze & import with AIArc": read all sheets, let AIArc pick the invoice-level ledger and map
  // it to the pipeline's invoice schema, extract the rows, and import straight into the scores &
  // tables. If AIArc can't find the invoice number / name / any rows, it opens a clarification chat.
  // Format-agnostic analysis of the WHOLE upload (all sheets across all files). AIArc classifies
  // each sheet by role (invoice lines / payments / aging summary / blocked / revenue) from content,
  // shows the user what it understood, and lets them correct it via chat before importing.
  // Build AIArc's chat turn from an analysis result — a conversational reply plus any clarifying
  // questions it wants the user to answer about the file.
  const assistantTurnFromResult = (result) => ({
    role: "assistant",
    content: (result && (result.reply || result.summary)) || "Here's what I understood — tell me if anything looks off.",
    questions: Array.isArray(result?.questions) ? result.questions : [],
  });

  const runWorkbookAnalysis = async (guidance = "", overrides = {}) => {
    const workbook = await readWorkbookForAnalysis(files);
    const result = await analyzeWorkbook(workbook, guidance);
    const formattedDate = result.asOfDate || new Date().toISOString().slice(0, 10);
    const dateISO = new Date(formattedDate).toISOString();
    const net = overrides.netSettlements != null ? overrides.netSettlements : netSettlements;
    const { records, paymentRecords } = await buildRecordsFromUnderstanding(result, files, dateISO, { netSettlements: net });
    return { result, formattedDate, dateISO, records, paymentRecords };
  };

  // Re-extract with the current toggles (no fresh AI call needed) — used when the user flips the
  // "net settlements" option so the preview reflects it immediately.
  const reExtractWithOptions = async (nextNet) => {
    if (!understanding?.result) return;
    setUnderstandingBusy(true);
    try {
      const dateISO = new Date(understanding.formattedDate).toISOString();
      const { records, paymentRecords } = await buildRecordsFromUnderstanding(understanding.result, files, dateISO, { netSettlements: nextNet });
      setPreview((p) => ({ ...(p || {}), records, paymentRecords, summary: summarizeRecords(records) }));
    } catch (e) {
      toast.error("Couldn't re-extract with that option.");
    } finally {
      setUnderstandingBusy(false);
    }
  };

  const handleAnalyzeWithClaude = async () => {
    if (!files.length) { toast.error("Add at least one file first"); return; }
    setAnalyzing(true);
    try {
      const { result, formattedDate, records, paymentRecords } = await runWorkbookAnalysis("");
      setAnalyzing(false);
      // Show what AIArc understood + the extracted rows, with a chat to correct it before importing.
      // Seed the chat with AIArc's opening message + any clarifying questions about the file.
      setUnderstanding({ result, formattedDate, messages: [assistantTurnFromResult(result)] });
      setPreview({ records, paymentRecords, formattedDate, summary: summarizeRecords(records), warnings: result.warnings || [], understanding: result });
    } catch (e) {
      setAnalyzing(false);
      const net = e?.message === "Network Error" || e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      toast.error(net
        ? "AIArc couldn't reach the analysis service in time. Please try again — or set the file type and map columns manually below."
        : (e?.response?.data?.message || e?.message || "AIArc analysis hit an issue — you can set the file type and map it manually below."));
    }
  };

  // The user corrected/guided AIArc from the review chat — re-run the analysis with their note.
  const applyUnderstandingGuidance = async (note) => {
    if (!understanding) return;
    // Send AIArc the full back-and-forth so it can answer follow-ups and honor prior corrections.
    const transcript = (understanding.messages || []).map((m) => `${m.role === "user" ? "User" : "AIArc"}: ${m.content}`);
    const guidance = [...transcript, `User: ${note}`].join("\n");
    // Show the user's message immediately (optimistic), then AIArc's reply once analysis returns.
    setUnderstanding((u) => ({ ...u, messages: [...(u?.messages || []), { role: "user", content: note }] }));
    setUnderstandingBusy(true);
    try {
      const { result, formattedDate, records, paymentRecords } = await runWorkbookAnalysis(guidance);
      setUnderstanding((u) => ({ result, formattedDate, messages: [...(u?.messages || []), assistantTurnFromResult(result)] }));
      setPreview({ records, paymentRecords, formattedDate, summary: summarizeRecords(records), warnings: result.warnings || [], understanding: result });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Couldn't apply that — try rephrasing.");
    } finally {
      setUnderstandingBusy(false);
    }
  };

  const handleDeleteFile = (fileName) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
    setDateSelections((prevSelections) => {
      const newSelections = { ...prevSelections };
      delete newSelections[fileName];
      return newSelections;
    })
    setFileTypes((prevTypes) => {
      const newTypes = { ...prevTypes };
      delete newTypes[fileName];
      return newTypes;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv, .xls, .xlsx",
    multiple: true,
  });

  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: "#2F3D63",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: "#2F3D63",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 0,
      border: "1px solid #2F3D63",
      backgroundColor: "#2F3D63",
      borderRadius: 1,
    },
  }));

  // Upload file to GCS using signed URL
  const uploadFileToGCS = async (file, fileType) => {
    try {
      // Get signed URL from underwriting API
      const signedUrlResponse = await companyApi.getAgingUploadSignedUrl({
        fileName: file.name,
        fileType: fileType,
        tenantId: tenantId
      });

      // Upload file to GCS using signed URL
      const uploadResponse = await fetch(signedUrlResponse.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file to GCS: ${uploadResponse.statusText}`);
      }

      return signedUrlResponse.gcsPath || signedUrlResponse.key;
    } catch (error) {
      console.error("Error uploading file to GCS: ", error);
      toast.error(`Error uploading ${file.name}`);
      throw error;
    }
  };

  const handleUpload = async () => {
    setLoading(true);
    const latestDate = getLatestDate();
    const formattedDate = latestDate.split("_").reverse().join("-");
    
    try {
      // Manual mapping now uses the same review + tracked import path as AIArc. Previously this
      // branch fired an untracked background request and immediately advanced to a blank success
      // screen, which hid the summary/chat and made the modal appear to simply close.
      if (
        tenantId !== "300358df-22a8-471b-863e-2bb84b5ef948" &&
        tenantId !== "notenant"
      ) {
        const { agingData, paymentData } = await mergeFiles(
          files,
          fileTypes,
          columnMappings,
          dateSelections,
          headerOffsets,
          dropLabels
        );
        if (!Array.isArray(agingData) || agingData.length === 0) {
          throw new Error("No AR rows were extracted. Please review the file type and column mapping.");
        }
        const summary = summarizeRecords(agingData);
        setUnderstanding({
          formattedDate,
          result: {
            summary: `Mapped ${summary.count.toLocaleString()} AR rows across ${summary.customers.toLocaleString()} customers. Review the extracted data before starting the import.`,
            sheets: files.map((file) => ({
              role: fileTypes[file.name] === "Payment File" ? "payments" : "aging_summary",
              fileName: file.name,
              sheetName: "Manual mapping",
              mappings: (columnMappings || []).find((m) => m.fileName === file.name) || {},
              rowCount: fileTypes[file.name] === "Payment File" ? (paymentData?.length || 0) : agingData.length,
            })),
          },
          messages: [{ role: "assistant", content: `I mapped the uploaded files and found ${summary.customers.toLocaleString()} customers. Check the sample and totals below. You can tell me here if a column or file was interpreted incorrectly before importing.` }],
        });
        setPreview({
          records: agingData,
          paymentRecords: paymentData || [],
          formattedDate,
          summary,
          warnings: [],
        });
        setActiveStep(0);
        toast.success("File mapped — review the summary before importing.");
        return;
      }

      // Update tenant aging date using tenantApi
      await tenantApi.updateTenant(tenantId, {
        agingUpdatedUpTo: new Date(formattedDate).toISOString(),
      });

      const tenantInfo = await tenantApi.getTenantById(tenantId);
      const arThreshold = tenantInfo.arThreshold;

      if (
        tenantId === "300358df-22a8-471b-863e-2bb84b5ef948" ||
        tenantId === "notenant"
      ) {
        const agingFile = files.find(file => fileTypes[file.name] === "Invoice File");
        const paymentFile = files.find(file => fileTypes[file.name] === "Payment File");

        const newAgingFile = new File(
          [await agingFile.arrayBuffer()],
          `${getLatestDate()}_aging_data.csv`,
          { type: "text/csv" }
        );

        const newPaymentFile = new File(
          [await paymentFile.arrayBuffer()],
          `${getLatestDate()}_payment_data.csv`,
          { type: "text/csv" }
        );

        const agingKey = await uploadFileToGCS(newAgingFile, "aging");
        const paymentKey = await uploadFileToGCS(newPaymentFile, "payment");

        // Process aging files
        await companyApi.processAgingFiles(
          tenantId, 
          agingKey, 
          paymentKey, 
          formattedDate,
          arThreshold
        );

      } else {
        const { agingData, paymentData } = await mergeFiles(
          files,
          fileTypes,
          columnMappings,
          dateSelections,
          headerOffsets,
          dropLabels
        );
      
        let agingKey = null;
        let paymentKey = null;
      
        if (agingData && agingData.length > 0) {
          const agingFile = new File(
            [JSON.stringify(agingData)],
            `${getLatestDate()}_aging_data.json`,
            { type: "application/json" }
          );
          agingKey = await uploadFileToGCS(agingFile, "aging");
        }
      
        if (paymentData && paymentData.length > 0) {
          const paymentFile = new File(
            [JSON.stringify(paymentData)],
            `${getLatestDate()}_payment_data.json`,
            { type: "application/json" }
          );
          paymentKey = await uploadFileToGCS(paymentFile, "payment");
        }
      
       try {
        if (agingKey || paymentKey) {
          // Process aging files
          await companyApi.processAgingFiles(
            tenantId, 
            agingKey, 
            paymentKey, 
            formattedDate, 
            arThreshold
          );
        } else {
          console.log("No valid aging or payment data to process.");
        }
       } catch (error) {
        console.log("Error Processing Aging And Payment", error)
       }
      }

      if (
        tenantId !== "300358df-22a8-471b-863e-2bb84b5ef948" &&
        tenantId !== "notenant"
      )
        setActiveStep(2);
      toast.success(`Files uploaded successfully!`);

    } catch (error) {
      console.error("Error during file upload: ", error);
      toast.error(error.message || "An unexpected error occurred.");
      
      // Revert tenant aging date on error using tenantApi
      try {
        await tenantApi.updateTenant(tenantId, {
          agingUpdatedUpTo: agingUpdatedUpTo,
        });
      } catch (revertError) {
        console.error("Error reverting tenant aging date: ", revertError);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (file) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      const agingFilesCount = files.filter(file => fileTypes[file.name] === "Invoice File").length;
      const paymentFilesCount = files.filter(file => fileTypes[file.name] === "Payment File").length;

      if (
        tenantId === "300358df-22a8-471b-863e-2bb84b5ef948" ||
        tenantId === "notenant"
      ) {
        if (agingFilesCount > 1 || paymentFilesCount > 1) {
          toast.error(
            "Please select only one set of Invoice File file and Payment File files"
          );
          return;
        }
        handleUpload();
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleButtonClick = () => {
    if (activeStep === 0) {
      handleNext();
    }
    if (
      tenantId === "300358df-22a8-471b-863e-2bb84b5ef948" ||
      tenantId === "notenant"
    ) {
      if (activeStep === 1) {
        handleClose();
      }
    } else {
      if (activeStep === 1) {
        handleUpload();
      } else if (activeStep === 2) {
        handleClose();
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Modal
        open={open}
        onClose={() => { if (!populating && !cancelling) handleClose(); }}
        aria-labelledby='file-upload-modal-title'
        aria-describedby='file-upload-modal-description'
        sx={modalStyle}>
        <Box
          sx={{
            display: "flex",
            height: "100vh",
            // The overlay itself scrolls; the card centers via margin:auto and scrolls when tall.
            overflowY: "auto",
            py: 3,
            border: "none",
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              if (!populating && !cancelling) handleClose();
            }
          }}>
          <Box sx={style}>
            <IconButton
              sx={{ position: "absolute", top: 10, left: 10 }}
              disabled={populating || cancelling}
              onClick={handleClose}>
              <img src={Close} alt='close button' />
            </IconButton>
            <Typography
              id='file-upload-modal-title'
              variant='h6'
              component='h2'
              sx={{
                textAlign: "center",
                padding: 2,
                color: "#2F3D63",
              }}>
              Add Files
            </Typography>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              connector={<ColorlibConnector />}
              sx={stepperStyle}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={ColorlibStepIcon}
                    icon={index + 1}></StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box
              sx={{
                margin: "20px auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}>
              {activeStep === 0 && (
                <Box sx={{ width: "90%" }}>
                  <Box {...getRootProps()} sx={dropzoneStyle}>
                    <input {...getInputProps()} />
                    {isDragActive ? (
                      <Typography variant='body1' color='primary'>
                        Drop the files here...
                      </Typography>
                    ) : (
                      <Typography variant='body1' color='primary'>
                        {tenantId === "300358df-22a8-471b-863e-2bb84b5ef948" || tenantId === "notenant" ? "Drag and drop or click to select to select 2 Files" : "Drag and drop files here, or click to select files"}
                      </Typography>
                    )}
                  </Box>
                  {files.length > 0 && (
                    <>
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: "10px", border: "1px solid #d5e0fb", background: "#f5f8ff", display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <Button variant="contained" size="small" onClick={handleAnalyzeWithClaude} disabled={analyzing}
                        startIcon={analyzing ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                        sx={{ textTransform: "none", fontWeight: 700, background: "#2f57c4" }}>
                        {analyzing ? "Importing…" : "✨ Analyze & import with AIArc"}
                      </Button>
                      <Typography sx={{ fontSize: 12.5, color: "#3a4358" }}>
                        Optional: AIArc, CrediArc's model, reads an aging file, maps every column, and imports it straight into the scores &amp; tables — one click, no manual mapping. Or set the file type and map columns manually below.
                      </Typography>
                    </Box>
                    {/* AR import options — set before analyzing. */}
                    <Box sx={{ mt: 1.25, px: 1.5, py: 1, borderRadius: "10px", border: "1px solid #e3e8f5", background: "#fbfcff" }}>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: "#64748b", mb: 0.25 }}>Import options</Typography>
                      <FormControlLabel sx={{ display: "flex", m: 0 }}
                        control={<Checkbox size="small" checked={ageByStatement} onChange={(e) => setAgeByStatement(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 12.5, color: "#334155" }}>Age by the statement date — not today (a bill not yet due as of the statement isn't counted overdue).</Typography>} />
                      <FormControlLabel sx={{ display: "flex", m: 0 }}
                        control={<Checkbox size="small" checked={netSettlements} onChange={(e) => setNetSettlements(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 12.5, color: "#334155" }}>Net settlements against invoices — paid invoices become payment history instead of phantom arrears.</Typography>} />
                      <FormControlLabel sx={{ display: "flex", m: 0 }}
                        control={<Checkbox size="small" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 12.5, color: "#334155" }}>Replace the existing AR — delete all current AR customers before importing this file.</Typography>} />
                    </Box>
                    </>
                  )}
                  {clarify && !preview && !populateLog && (
                    <Box sx={{ mt: 2 }}>
                      <AiClarifyChat
                        grid={clarify.grid}
                        seedMessage={clarify.seed}
                        schema={clarify.invoiceMode ? "invoice" : "aging"}
                        importing={clarifyImporting}
                        onResolved={applyResolvedAndImport}
                        onCancel={() => setClarify(null)}
                      />
                    </Box>
                  )}

                  {/* Populate log — shown once the user approves and the import runs. */}
                  {populateLog && (
                    <Box sx={{ mt: 2, border: "1px solid #d5e0fb", borderRadius: "12px", overflow: "hidden" }}>
                      <Box sx={{ px: 2, py: 1.25, background: "#f5f8ff", borderBottom: "1px solid #e3e6ee", display: "flex", alignItems: "center", gap: 1 }}>
                        {populating ? <CircularProgress size={16} sx={{ color: "#2f57c4" }} /> : <span style={{ color: "#2e7d32", fontWeight: 700 }}>✓</span>}
                        <Typography sx={{ fontWeight: 700, color: "#1f2a44", fontSize: 14 }}>{populating ? "Importing…" : "Import finished"}</Typography>
                      </Box>
                      {/* Live underwriting progress bar + counter */}
                      {scoring && scoring.total > 0 && (
                        <Box sx={{ px: 2, pt: 1.25, pb: 0.75, borderBottom: "1px solid #eef0f5", background: "#fbfcff" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#1f2a44" }}>
                              Underwriting customers — {scoring.scored} of {scoring.total} scored
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>{Math.max(scoring.total - scoring.scored, 0)} left</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(100, Math.round((scoring.scored / scoring.total) * 100))} sx={{ height: 8, borderRadius: 4 }} />
                          {Array.isArray(scoring.inProgressNames) && scoring.inProgressNames.length > 0 && (
                            <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.5 }} noWrap>
                              Now: {scoring.inProgressNames.slice(0, 3).join(", ")}{scoring.inProgressNames.length > 3 ? "…" : ""}
                            </Typography>
                          )}
                        </Box>
                      )}
                      <Box sx={{ maxHeight: 240, overflowY: "auto", p: 1.5, fontFamily: "monospace", fontSize: 12.5, background: "#fff" }}>
                        {populateLog.map((l, i) => (
                          <Box key={i} sx={{ display: "flex", gap: 1, py: 0.35, color: l.status === "error" ? "#c0392b" : l.status === "warn" ? "#b7791f" : l.status === "done" ? "#2e7d32" : "#3a4358" }}>
                            <span style={{ color: "#9aa3b8" }}>{l.ts}</span>
                            <span>{l.status === "done" ? "✓" : l.status === "error" ? "✕" : l.status === "warn" ? "!" : "•"}</span>
                            <span style={{ flex: 1 }}>{l.msg}</span>
                          </Box>
                        ))}
                      </Box>
                      <Box sx={{ p: 1.25, borderTop: "1px solid #eef0f5", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, background: "#fff" }}>
                        <Box>
                          {importId && (
                            <Button onClick={cancelImport} disabled={cancelling} startIcon={cancelling ? <CircularProgress size={14} sx={{ color: "#c0392b" }} /> : null}
                              sx={{ textTransform: "none", fontWeight: 700, color: "#c0392b" }}>
                              {cancelling ? "Killing…" : "Kill process"}
                            </Button>
                          )}
                        </Box>
                        {!populating && (
                          <Button variant="contained" onClick={handleClose} disabled={cancelling} sx={{ textTransform: "none", fontWeight: 700, background: "#2f57c4" }}>Done</Button>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* What AIArc understood about the whole upload — shown before import, with a chat
                      so the client can correct it (format differs per client, so this is the key step). */}
                  {understanding && !populateLog && (
                    <Box sx={{ mt: 2, border: "1px solid #cfe0ff", borderRadius: "12px", overflow: "hidden" }}>
                      <Box sx={{ px: 2, py: 1.5, background: "#eef4ff", borderBottom: "1px solid #dce7fb" }}>
                        <Typography sx={{ fontWeight: 800, color: "#1f2a44", fontSize: 15 }}>What AIArc understood about your upload</Typography>
                        {understanding.result?.summary && (
                          <Typography sx={{ fontSize: 12.5, color: "#3a4358", mt: 0.5 }}>{understanding.result.summary}</Typography>
                        )}
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        {(understanding.result?.sheets || []).length === 0 ? (
                          <Typography sx={{ fontSize: 12.5, color: "#b7791f" }}>AIArc couldn't confidently classify any sheet — use the chat below to point it to the right data.</Typography>
                        ) : (
                          (understanding.result.sheets || []).map((s, i) => (
                            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, flexWrap: "wrap" }}>
                              <Chip size="small" label={(ROLE_META[s.role] || {}).label || s.role} sx={{ height: 20, fontWeight: 700, color: "#fff", background: (ROLE_META[s.role] || {}).color || "#64748b" }} />
                              <Typography sx={{ fontSize: 12.5, color: "#1f2a44", fontWeight: 600 }}>{s.fileName} › {s.sheetName}</Typography>
                              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>{Object.keys(s.mappings || {}).length} columns mapped · {(s.rowCount || 0).toLocaleString()} rows</Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                      <Box sx={{ p: 1.25, borderTop: "1px solid #eef0f5", background: "#fff" }}>
                        {/* Chatbot — correct AIArc if something's wrong, or answer its clarifying questions about the file. */}
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#64748b", mb: 0.75 }}>💬 Chat with AIArc — spot a mistake or need to explain the file? Ask here.</Typography>
                        <Box sx={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.75, mb: 1, pr: 0.5 }}>
                          {(understanding.messages || []).map((m, i) => (
                            <Box key={i} sx={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%" }}>
                              <Box sx={{
                                px: 1.25, py: 0.75, borderRadius: "12px",
                                background: m.role === "user" ? "#2563eb" : "#f1f5f9",
                                color: m.role === "user" ? "#fff" : "#1f2a44",
                                border: m.role === "user" ? "none" : "1px solid #e3e8f2",
                              }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, opacity: 0.7, mb: 0.25 }}>{m.role === "user" ? "You" : "AIArc"}</Typography>
                                <Typography sx={{ fontSize: 12.5, whiteSpace: "pre-wrap" }}>{m.content}</Typography>
                                {Array.isArray(m.questions) && m.questions.length > 0 && (
                                  <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.4 }}>
                                    {m.questions.map((q, qi) => (
                                      <Box key={qi} onClick={() => { if (!understandingBusy) setGuidanceInput(q + " — "); }}
                                        sx={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#1d4ed8", background: "#eaf1ff", border: "1px solid #cfe0ff", borderRadius: "8px", px: 1, py: 0.5, "&:hover": { background: "#dfeaff" } }}>
                                        ❓ {q}
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          ))}
                          {understandingBusy && (
                            <Box sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 1, color: "#64748b", px: 0.5 }}>
                              <CircularProgress size={13} /> <Typography sx={{ fontSize: 12 }}>AIArc is reviewing…</Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <TextField size="small" fullWidth placeholder="Message AIArc — e.g. 'blocked customers are in the Hold sheet', 'column X is the due date', 'this file is only payments'"
                            value={guidanceInput} onChange={(e) => setGuidanceInput(e.target.value)} disabled={understandingBusy}
                            onKeyDown={(e) => { if (e.key === "Enter" && guidanceInput.trim()) { const n = guidanceInput.trim(); setGuidanceInput(""); applyUnderstandingGuidance(n); } }} />
                          <Button variant="contained" disabled={understandingBusy || !guidanceInput.trim()}
                            onClick={() => { const n = guidanceInput.trim(); setGuidanceInput(""); applyUnderstandingGuidance(n); }}
                            sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
                            {understandingBusy ? <CircularProgress size={16} /> : "Send"}
                          </Button>
                        </Box>
                        {/* AR aging options */}
                        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: "1px dashed #e3e8f5", display: "flex", flexDirection: "column", gap: 0.25 }}>
                          <FormControlLabel
                            control={<Checkbox size="small" checked={ageByStatement} disabled={understandingBusy} onChange={(e) => setAgeByStatement(e.target.checked)} />}
                            label={<Typography sx={{ fontSize: 12.5, color: "#334155" }}>Age by the statement date{understanding.formattedDate ? ` (${understanding.formattedDate})` : ""} — not today. Recommended: a bill that wasn't due as of the statement isn't counted overdue.</Typography>}
                          />
                          <FormControlLabel
                            control={<Checkbox size="small" checked={netSettlements} disabled={understandingBusy} onChange={(e) => { setNetSettlements(e.target.checked); reExtractWithOptions(e.target.checked); }} />}
                            label={<Typography sx={{ fontSize: 12.5, color: "#334155" }}>Net settlements against invoices — fold each invoice's paying journal/credit-memo rows into it, so paid invoices become payment history instead of phantom arrears.</Typography>}
                          />
                        </Box>
                        <Typography sx={{ fontSize: 11, color: "#9aa3b8", mt: 0.75 }}>Review the extracted rows below, correct anything above, then import.</Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Preview & approve — extracted rows shown before anything is imported. */}
                  {preview && !populateLog && (
                    <Box sx={{ mt: 2, border: "1px solid #d5e0fb", borderRadius: "12px", overflow: "hidden" }}>
                      <Box sx={{ px: 2, py: 1.5, background: "#f5f8ff", borderBottom: "1px solid #e3e6ee" }}>
                        <Typography sx={{ fontWeight: 800, color: "#1f2a44", fontSize: 15 }}>Review extracted data</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#3a4358", mt: 0.25 }}>
                          AIArc extracted <strong>{preview.summary.count.toLocaleString()}</strong> AR lines across <strong>{preview.summary.customers}</strong> customers · total open balance <strong>{toMoney(preview.summary.total)}</strong>{preview.sheetName ? ` (from the "${preview.sheetName}" sheet)` : ""}. Confirm it looks right, then import.
                        </Typography>
                        {Array.isArray(preview.warnings) && preview.warnings.length > 0 && (
                          <Typography sx={{ fontSize: 12, color: "#b7791f", mt: 0.5 }}>Note: {preview.warnings[0]}</Typography>
                        )}
                      </Box>
                      <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { textAlign: "left", py: 0.6, px: 1, borderBottom: "1px solid #eef0f5", fontSize: 12.5, whiteSpace: "nowrap" } }}>
                          <Box component="thead">
                            <Box component="tr" sx={{ "& th": { position: "sticky", top: 0, background: "#f8fafc", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 700 } }}>
                              <th>Customer</th><th>Document</th><th>Date</th><th>Due date</th><th style={{ textAlign: "right" }}>Open balance</th>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {preview.records.slice(0, 100).map((r, i) => (
                              <Box component="tr" key={i}>
                                <td style={{ fontWeight: 600, color: "#1f2a44" }}>{r.name}</td>
                                <td style={{ color: "#64748b" }}>{r.documentNumber}</td>
                                <td style={{ color: "#64748b" }}>{String(r.invoiceDate || "").slice(0, 10)}</td>
                                <td style={{ color: "#64748b" }}>{String(r.dueDate || "").slice(0, 10)}</td>
                                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{toMoney(parseNum(r.amountDue) || parseNum(r.invoiceAmount))}</td>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        {preview.records.length > 100 && (
                          <Typography sx={{ fontSize: 11.5, color: "#9aa3b8", p: 1 }}>Showing the first 100 of {preview.records.length.toLocaleString()} lines.</Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.25, borderTop: "1px solid #eef0f5", background: "#fff", gap: 1 }}>
                        <Button onClick={() => { setPreview(null); setUnderstanding(null); }} sx={{ textTransform: "none", color: "#7a8398" }}>Cancel / map manually</Button>
                        <Button variant="contained" onClick={startPopulate} sx={{ textTransform: "none", fontWeight: 700, background: "#2e7d32" }}>Looks right — import</Button>
                      </Box>
                    </Box>
                  )}
                  <Box
                    sx={{
                      mt: 2,
                      textAlign: "left",
                      width: "100%",
                      color: "#2F3D63",
                      fontWeight: "bold",
                    }}>
                    {files.map((file, index) => (
                      <Box
                        key={file.name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1,
                          justifyContent: "space-between",
                        }}>
                        <Typography variant='heading2' sx={{ mr: 2 }}>
                          {index + 1}. {file.name}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}>
                          <FormControl
                            variant='outlined'
                            size='small'
                            sx={{ mr: 1, minWidth: "212px" }}>
                            <InputLabel id={`file-type-label-${file.name}`}>
                              File Type
                            </InputLabel>
                            <Select
                              labelId={`file-type-label-${file.name}`}
                              value={fileTypes[file.name] || ""}
                              onChange={(event) =>
                                handleFileTypeChange(file.name, event.target.value)
                              }
                              label='File Type'
                              sx={{ height: "40px", color: "#2F3D63" }}>
                              <MenuItem value='Invoice File' sx={{ width: "210px" }}>
                                Invoice File
                              </MenuItem>
                              <MenuItem value='Payment File'>
                                Payment File
                              </MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant='outlined'
                            color='error'
                            size='small'
                            onClick={() => handleDeleteFile(file.name)}
                            sx={{
                              minWidth: "auto",
                              padding: "4px",
                              height: "40px",
                            }}>
                            <img
                              src={BinIcon}
                              alt='Delete'
                              style={{ width: "16px", height: "16px" }}
                            />
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {openDatePicker && (
                    <DatePicker
                      open={openDatePicker}
                      onClose={() => setOpenDatePicker(false)}
                      onAccept={handleDateChange}
                      value={dateSelections[currentFile] ? dayjs(dateSelections[currentFile]) : null}
                      renderInput={(params) => (
                        <TextField {...params} sx={{ display: "none" }} />
                      )}
                    />
                  )}
                </Box>
              )}

              {    (tenantId !== "300358df-22a8-471b-863e-2bb84b5ef948" &&
        tenantId !== "notenant") && activeStep === 1 && (
                <Box sx={{ width: "90%" }}>
                  <Typography
                    variant='body1'
                    sx={{ mb: 2, color: "#2F3D63", fontSize: "12px" }}>
                    In order to process the file, we kindly ask for your
                    assistance in ensuring that the columns are correctly
                    identified. Your help is greatly appreciated.
                  </Typography>

                  {files.map((file, index) => {
                    const fileType = fileTypes[file.name];
                    return (
                      <Accordion
                        key={file.name}
                        sx={{ mb: 2 }}
                        expanded={expandedAccordion === file.name}
                        onChange={handleAccordionChange(file.name)}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`${file.name}-content`}
                          id={`${file.name}-header`}>
                          <Typography variant='p' sx={{ color: "#2F3D63" }}>
                            <span style={{ fontWeight: "bold" }}>
                              {index + 1}. {file.name}
                            </span>{" "}
                            - {fileType}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{ maxHeight: "250px", overflowY: "auto" }}>
                          {fileType === "Invoice File" && (
                            <HistoricalPaymentColumnMapper
                              file={file}
                              setColumnMappings={setColumnMappings}
                            />
                          )}
                          {fileType === "Payment File" && (
                            <HistoricalPaymentColumnMapper
                              file={file}
                              setColumnMappings={setColumnMappings}
                            />
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}
  
              {(    (tenantId === "300358df-22a8-471b-863e-2bb84b5ef948" ||
    tenantId === "notenant") && activeStep === 1) || 
               (    (tenantId !== "300358df-22a8-471b-863e-2bb84b5ef948" &&
                tenantId !== "notenant") && activeStep === 2) ? (
                <Typography variant='body2' color='primary' sx={{ mt: 2 }}>
                  Files uploaded successfully!
                </Typography>
              ) : null}
  
              <Box sx={{ display: 'flex', gap: 2 }}>
                {    (tenantId === "300358df-22a8-471b-863e-2bb84b5ef948" ||
    tenantId === "notenant") ? (
                  <>
                    {activeStep===0?<Button
                      variant='outlined'
                      sx={{
                        ...buttonStyle,
                        backgroundColor: "#fff",
                        color: "#5186EC",
                        borderColor: "#5186EC",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          borderColor: "#4069D0",
                        },
                      }}
                      onClick={handleClose}>
                      Cancel
                    </Button>:null}
                    <Button
                      variant='contained'
                      sx={buttonStyle}
                      disabled={files.length === 0 || loading}
                      onClick={handleButtonClick}>
                      {activeStep === 0 ? "Upload" : "Close"}
                    </Button>
                  </>
                ) : (
                  <>
                    {activeStep!==2?<Button
                      variant='outlined'
                      sx={{
                        ...buttonStyle,
                        backgroundColor: "#fff",
                        color: "#5186EC",
                        borderColor: "#5186EC",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          borderColor: "#4069D0",
                        },
                      }}
                      disabled={activeStep === 0}
                      onClick={handleBack}>
                      Back
                    </Button>:null}
                    <Button
                      variant='contained'
                      sx={buttonStyle}
                      disabled={files.length === 0 || loading || activeStep === 3}
                      onClick={handleButtonClick}>
                      {activeStep === 0
                        ? "Next"
                        : activeStep === 1
                        ? "Upload"
                        : "Close"}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </LocalizationProvider>
  );
};

// The existing upload entry point selects the tenant's server-authorized flow.
// A lookup failure never falls through into the legacy replacement/underwriting path.
export default function ScopedFileUploadModal(props) {
  const currentTenant = useSelector(state => state.userInfo.tenantId);
  const tenantId = getTenantIdFromURL() || currentTenant;
  const [scope, setScope] = useState(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setScope(null);
    if (props.open && tenantId) getReceivablesCapabilities(tenantId)
      .then(capabilities => { if (active) setScope({ tenantId, capabilities }); })
      .catch(() => { if (active) setScope({ tenantId, error: true }); });
    return () => { active = false; };
  }, [props.open, tenantId, retry]);
  if (!props.open) return null;
  const current = scope?.tenantId === tenantId ? scope : null;
  if (current?.capabilities?.enabled) return <ReceivablesImportWizard key={tenantId} open={props.open} onClose={props.close} tenantId={tenantId} onCommitted={result => {
    publish('ar_dashboard_refresh', { tenantId, importId: result?.importId });
    publish('ar_import_started', { tenantId, importId: result?.importId });
  }} />;
  if (current?.capabilities?.enabled === false) return <FileUploadModal {...props} />;
  return <Modal open onClose={props.close}><Box sx={{ ...style, mt: '15vh', p: 3 }}>
    <Typography variant="h6">Receivables import</Typography>
    <Typography sx={{ my: 2 }}>{current?.error || !tenantId ? 'Import access could not be verified. Select a tenant and try again.' : 'Checking import access…'}</Typography>
    {(current?.error || !tenantId) ? <Button onClick={() => setRetry(value => value + 1)}>Try again</Button> : <CircularProgress size={24} />}
    <Button onClick={props.close}>Close</Button>
  </Box></Modal>;
}
